/**
 * Frontend translations for peptide research metadata.
 * Values come from Saleor product metadata (may be English or German),
 * translated here to avoid modifying the API data model.
 */
import { getContentFallbackChainOf } from "@/config/locale";
import type { PeptideMetadata } from "@/lib/peptide-metadata";
import { isCanonicalTrustBadge, resolveTrustBadgeAlias } from "@/lib/trust-badges";
import { countBefore, selectUnitLabel, type UnitLabel } from "@/lib/unit-label";

/**
 * Entry of a locale-keyed table along the locale's content chain (es-mx: its own entry, then es),
 * before the caller's English or source fallback. Every non-regional locale reads exactly its own
 * entry, as before.
 */
function pickByContentChain<T>(entries: Record<string, T> | undefined, locale: string): T | undefined {
	if (!entries) return undefined;
	for (const candidate of getContentFallbackChainOf(locale)) {
		const value = entries[candidate];
		if (value != null) return value;
	}
	return undefined;
}

const RESEARCH_LEVEL_LABELS: Record<string, Record<string, string>> = {
	limited: {
		de: "Begrenzt",
		en: "Limited",
		bg: "Ограничено",
		cs: "Omezená",
		da: "Begrænset",
		el: "Περιορισμένο",
		es: "Limitado",
		fi: "Rajoitettu",
		fr: "Limité",
		hr: "Ograničeno",
		sl: "Omejeno",
		sr: "Ograničeno",
		bs: "Ograničeno",
		mk: "Ограничено",
		lt: "Ribotas",
		lv: "Ierobežots",
		et: "Piiratud",
		hu: "Korlátozott",
		it: "Limitato",
		nb: "Begrenset",
		nl: "Beperkt",
		pl: "Ograniczony",
		pt: "Limitado",
		"pt-br": "Limitado",
		ro: "Limitat",
		sk: "Obmedzené",
		sv: "Begränsad",
		tr: "Sınırlı",
	},
	moderate: {
		de: "Moderat",
		en: "Moderate",
		bg: "Умерено",
		cs: "Střední",
		da: "Moderat",
		el: "Μέτριο",
		es: "Moderado",
		fi: "Kohtalainen",
		fr: "Modéré",
		hr: "Umjereno",
		sl: "Zmerno",
		sr: "Umereno",
		bs: "Umjereno",
		mk: "Умерено",
		lt: "Vidutinis",
		lv: "Mērens",
		et: "Mõõdukas",
		hu: "Mérsékelt",
		it: "Moderato",
		nb: "Moderat",
		nl: "Matig",
		pl: "Umiarkowany",
		pt: "Moderado",
		"pt-br": "Moderado",
		ro: "Moderat",
		sk: "Stredné",
		sv: "Måttlig",
		tr: "Orta",
	},
	extensive: {
		de: "Umfangreich",
		en: "Extensive",
		bg: "Обширно",
		cs: "Rozsáhlá",
		da: "Omfattende",
		el: "Εκτεταμένο",
		es: "Extenso",
		fi: "Laaja",
		fr: "Étendu",
		hr: "Opsežno",
		sl: "Obsežno",
		sr: "Opsežno",
		bs: "Opsežno",
		mk: "Опсежно",
		lt: "Platus",
		lv: "Plašs",
		et: "Ulatuslik",
		hu: "Kiterjedt",
		it: "Esteso",
		nb: "Omfattende",
		nl: "Uitgebreid",
		pl: "Obszerny",
		pt: "Extenso",
		"pt-br": "Extenso",
		ro: "Extins",
		sk: "Rozsiahle",
		sv: "Omfattande",
		tr: "Kapsamlı",
	},
	established: {
		de: "Etabliert",
		en: "Established",
		bg: "Установено",
		cs: "Ověřená",
		da: "Etableret",
		el: "Καθιερωμένο",
		es: "Establecido",
		fi: "Vakiintunut",
		fr: "Établi",
		hr: "Utvrđeno",
		sl: "Uveljavljeno",
		sr: "Utvrđeno",
		bs: "Utvrđeno",
		mk: "Утврдено",
		lt: "Nusistovėjęs",
		lv: "Etablēts",
		et: "Väljakujunenud",
		hu: "Kialakult",
		it: "Consolidato",
		nb: "Etablert",
		nl: "Gevestigd",
		pl: "Ustabilizowany",
		pt: "Estabelecido",
		"pt-br": "Estabelecido",
		ro: "Consolidat",
		sk: "Etablované",
		sv: "Etablerad",
		tr: "Yerleşik",
	},
	emerging: {
		de: "Aufkommend",
		en: "Emerging",
		bg: "Възникващо",
		cs: "Vznikající",
		da: "Fremvoksende",
		el: "Αναδυόμενο",
		es: "Emergente",
		fi: "Kehittyvä",
		fr: "Émergent",
		hr: "Rastuće",
		sl: "Nastajajoče",
		sr: "Rastuće",
		bs: "Rastuće",
		mk: "Растечко",
		lt: "Atsirandantis",
		lv: "Topošs",
		et: "Tärkav",
		hu: "Feltörekvő",
		it: "Emergente",
		nb: "Fremvoksende",
		nl: "Opkomend",
		pl: "Wschodzący",
		pt: "Emergente",
		"pt-br": "Emergente",
		ro: "Emergent",
		sk: "Vznikajúce",
		sv: "Framväxande",
		tr: "Yeni Ortaya Çıkan",
	},
	experimental: {
		de: "Experimentell",
		en: "Experimental",
		bg: "Експериментално",
		cs: "Experimentální",
		da: "Eksperimentel",
		el: "Πειραματικό",
		es: "Experimental",
		fi: "Kokeellinen",
		fr: "Expérimental",
		hr: "Eksperimentalno",
		sl: "Eksperimentalno",
		sr: "Eksperimentalno",
		bs: "Eksperimentalno",
		mk: "Експериментално",
		lt: "Eksperimentinis",
		lv: "Eksperimentāls",
		et: "Eksperimentaalne",
		hu: "Kísérleti",
		it: "Sperimentale",
		nb: "Eksperimentell",
		nl: "Experimenteel",
		pl: "Eksperymentalny",
		pt: "Experimental",
		"pt-br": "Experimental",
		ro: "Experimental",
		sk: "Experimentálne",
		sv: "Experimentell",
		tr: "Deneysel",
	},
};

const AVAILABILITY_LABELS: Record<string, Record<string, string>> = {
	// This legacy field does not establish prescription availability for a
	// research product. French cards and existing filters retain research framing.
	"Prescription Available": {
		fr: "Recherche uniquement",
	},
	"Research Only": {
		de: "Nur für Forschung",
		en: "Research Only",
		bg: "Само за изследователски цели",
		cs: "Pouze pro výzkum",
		da: "Kun til forskning",
		el: "Μόνο για έρευνα",
		es: "Solo para investigación",
		fi: "Vain tutkimuskäyttöön",
		fr: "Recherche uniquement",
		hr: "Samo za istraživanje",
		sl: "Samo za raziskave",
		sr: "Samo za istraživanje",
		bs: "Samo za istraživanje",
		mk: "Само за истражувачки цели",
		lt: "Tik moksliniams tyrimams",
		lv: "Tikai pētniecībai",
		et: "Ainult teadusuuringuteks",
		hu: "Csak kutatási célra",
		it: "Solo per ricerca",
		nb: "Kun til forskning",
		nl: "Alleen voor onderzoek",
		pl: "Tylko do badań",
		pt: "Apenas para investigação",
		"pt-br": "Apenas para pesquisa",
		ro: "Doar pentru cercetare",
		sk: "Len na výskum",
		sv: "Endast forskning",
		tr: "Yalnızca Araştırma",
	},
	Compoundable: {
		de: "Rezepturfähig",
		en: "Compoundable",
		bg: "За магистрална рецептура",
		cs: "Recepturní",
		da: "Kan fremstilles på apotek",
		el: "Παρασκευάσιμο σε φαρμακείο",
		es: "Formulable",
		fi: "Apteekkivalmiste",
		fr: "Préparable en officine",
		hr: "Pripravljivo u ljekarni",
		sl: "Pripravljivo v lekarni",
		sr: "Pripravljivo u apoteci",
		bs: "Pripravljivo u ljekarni",
		mk: "Магистрална изработка",
		lt: "Paruošiama vaistinėje",
		lv: "Pagatavojams aptiekā",
		et: "Apteegis valmistatav",
		hu: "Magisztrálisan készíthető",
		it: "Preparabile in farmacia",
		nb: "Kan tilvirkes på apotek",
		nl: "Bereidbaar",
		pl: "Do receptury",
		pt: "Manipulável",
		"pt-br": "Manipulável",
		ro: "Preparabil în farmacie",
		sk: "Prípravovateľné v lekárni",
		sv: "Apoteksberedbar",
		tr: "Eczane Formülasyonuna Uygun",
	},
};

/**
 * Research-area labels for `ux_peptide_categories_json` values.
 * Closed value set — enumerated live from the eu catalog (2026-08-11).
 * Raw values are English display strings; NEVER render them untranslated.
 */
const PEPTIDE_CATEGORY_LABELS: Record<string, Record<string, string>> = {
	"Hormonal Health": {
		fr: "Signalisation hormonale",
		sl: "Hormonska signalizacija",
		sr: "Hormonska signalizacija",
		bs: "Hormonska signalizacija",
		mk: "Хормонална сигнализација",
		lt: "Hormonų signalizacija",
		lv: "Hormonālā signalizācija",
		et: "Hormonaalne signalisatsioon",
	},
	"Weight Management": {
		de: "Gewichtsmanagement",
		en: "Weight Management",
		bg: "Управление на теглото",
		cs: "Regulace hmotnosti",
		da: "Vægtkontrol",
		el: "Διαχείριση βάρους",
		es: "Control de peso",
		fi: "Painonhallinta",
		fr: "Gestion du poids",
		hr: "Upravljanje težinom",
		sl: "Obvladovanje telesne teže",
		sr: "Kontrola telesne mase",
		bs: "Kontrola tjelesne mase",
		mk: "Контрола на телесната тежина",
		lt: "Svorio kontrolė",
		lv: "Ķermeņa masas pārvaldība",
		et: "Kehakaalu haldamine",
		hu: "Testsúlykezelés",
		it: "Gestione del peso",
		nb: "Vektkontroll",
		nl: "Gewichtsbeheer",
		pl: "Zarządzanie masą ciała",
		pt: "Gestão de Peso",
		"pt-br": "Gestão de peso",
		ro: "Managementul greutății",
		sk: "Manažment hmotnosti",
		sv: "Viktkontroll",
		tr: "Kilo Yönetimi",
	},
	Recovery: {
		de: "Regeneration",
		en: "Recovery",
		bg: "Възстановяване",
		cs: "Regenerace",
		da: "Restitution",
		el: "Αποκατάσταση",
		es: "Recuperación",
		fi: "Palautuminen",
		fr: "Récupération",
		hr: "Regeneracija",
		sl: "Regeneracija",
		sr: "Oporavak i regeneracija",
		bs: "Oporavak i regeneracija",
		mk: "Закрепнување",
		lt: "Atsigavimas ir regeneracija",
		lv: "Atjaunošanās un reģenerācija",
		et: "Taastumine",
		hu: "Regeneráció",
		it: "Recupero",
		nb: "Restitusjon",
		nl: "Herstel",
		pl: "Regeneracja",
		pt: "Recuperação",
		"pt-br": "Recuperação",
		ro: "Regenerare",
		sk: "Regenerácia",
		sv: "Återhämtning",
		tr: "Toparlanma",
	},
	Skin: {
		de: "Haut",
		en: "Skin",
		bg: "Кожа",
		cs: "Pokožka",
		da: "Hud",
		el: "Δέρμα",
		es: "Piel",
		fi: "Iho",
		fr: "Peau",
		hr: "Koža",
		sl: "Koža",
		sr: "Koža i kosa",
		bs: "Koža",
		mk: "Кожа",
		lt: "Oda ir plaukai",
		lv: "Āda",
		et: "Nahk",
		hu: "Bőr",
		it: "Pelle",
		nb: "Hud",
		nl: "Huid",
		pl: "Skóra",
		pt: "Pele",
		"pt-br": "Pele",
		ro: "Piele",
		sk: "Pokožka",
		sv: "Hud",
		tr: "Cilt",
	},
	"Growth Hormone": {
		de: "Wachstumshormon",
		en: "Growth Hormone",
		bg: "Растежен хормон",
		cs: "Růstový hormon",
		da: "Væksthormon",
		el: "Αυξητική ορμόνη",
		es: "Hormona del crecimiento",
		fi: "Kasvuhormoni",
		fr: "Hormone de croissance",
		hr: "Hormon rasta",
		sl: "Rastni hormon",
		sr: "Hormon rasta",
		bs: "Hormon rasta",
		mk: "Хормон на раст",
		lt: "Augimo hormonas",
		lv: "Augšanas hormons",
		et: "Kasvuhormoon",
		hu: "Növekedési hormon",
		it: "Ormone della crescita",
		nb: "Veksthormon",
		nl: "Groeihormoon",
		pl: "Hormon wzrostu",
		pt: "Hormona do Crescimento",
		"pt-br": "Hormônio do crescimento",
		ro: "Hormon de creștere",
		sk: "Rastový hormón",
		sv: "Tillväxthormon",
		tr: "Büyüme Hormonu",
	},
	Healing: {
		de: "Heilung",
		en: "Healing",
		bg: "Заздравяване",
		cs: "Hojení",
		da: "Heling",
		el: "Επούλωση",
		es: "Cicatrización",
		fi: "Paraneminen",
		fr: "Cicatrisation",
		hr: "Cijeljenje",
		sl: "Celjenje",
		sr: "Zarastanje",
		bs: "Cijeljenje",
		mk: "Заздравување",
		lt: "Gijimas",
		lv: "Dzīšana",
		et: "Paranemine",
		hu: "Gyógyulás",
		it: "Guarigione",
		nb: "Heling",
		nl: "Wondgenezing",
		pl: "Gojenie",
		pt: "Cicatrização",
		"pt-br": "Cicatrização",
		ro: "Vindecare",
		sk: "Hojenie",
		sv: "Läkning",
		tr: "İyileşme",
	},
	"Anti-Aging": {
		de: "Anti-Aging",
		en: "Anti-Aging",
		bg: "Анти-ейджинг",
		cs: "Anti-Aging",
		da: "Anti-aging",
		el: "Anti-Aging",
		es: "Anti-Aging",
		fi: "Anti-aging",
		fr: "Anti-âge",
		hr: "Anti-aging",
		sl: "Proti staranju",
		sr: "Anti-aging",
		bs: "Anti-aging",
		mk: "Анти-стареење",
		lt: "Priešsenėjimas",
		lv: "Pretnovecošanās",
		et: "Vananemisvastasus",
		hu: "Anti-Aging",
		it: "Anti-Aging",
		nb: "Anti-aldring",
		nl: "Anti-Aging",
		pl: "Anti-Aging",
		pt: "Anti-Aging",
		"pt-br": "Anti-Aging",
		ro: "Anti-aging",
		sk: "Anti-Aging",
		sv: "Anti-Aging",
		tr: "Anti-Aging",
	},
	Performance: {
		de: "Leistung",
		en: "Performance",
		bg: "Работоспособност",
		cs: "Výkon",
		da: "Præstation",
		el: "Απόδοση",
		es: "Rendimiento",
		fi: "Suorituskyky",
		fr: "Performance",
		hr: "Performanse",
		sl: "Zmogljivost",
		sr: "Performanse",
		bs: "Performanse",
		mk: "Перформанси",
		lt: "Fizinis pajėgumas",
		lv: "Veiktspēja",
		et: "Jõudlus",
		hu: "Teljesítmény",
		it: "Performance",
		nb: "Ytelse",
		nl: "Prestatie",
		pl: "Wydolność",
		pt: "Desempenho",
		"pt-br": "Performance",
		ro: "Performanță",
		sk: "Výkonnosť",
		sv: "Prestation",
		tr: "Performans",
	},
	"Anti-Inflammatory": {
		de: "Entzündungshemmend",
		en: "Anti-Inflammatory",
		bg: "Противовъзпалително",
		cs: "Protizánětlivé",
		da: "Antiinflammatorisk",
		el: "Αντιφλεγμονώδη",
		es: "Antiinflamatorio",
		fi: "Anti-inflammatorinen",
		fr: "Anti-inflammatoire",
		hr: "Protuupalno djelovanje",
		sl: "Protivnetno delovanje",
		sr: "Protivupalno delovanje",
		bs: "Protivupalno djelovanje",
		mk: "Противвоспалително",
		lt: "Priešuždegiminis poveikis",
		lv: "Pretiekaisuma darbība",
		et: "Põletikuvastane",
		hu: "Gyulladáscsökkentő",
		it: "Anti-infiammatorio",
		nb: "Betennelsesdempende",
		nl: "Ontstekingsremmend",
		pl: "Przeciwzapalne",
		pt: "Anti-Inflamatório",
		"pt-br": "Anti-inflamatório",
		ro: "Antiinflamator",
		sk: "Protizápalové",
		sv: "Antiinflammatorisk",
		tr: "Anti-İnflamatuar",
	},
	"Gut Health": {
		de: "Darmgesundheit",
		en: "Gut Health",
		bg: "Здраве на червата",
		cs: "Zdraví střev",
		da: "Tarmsundhed",
		el: "Υγεία εντέρου",
		es: "Salud intestinal",
		fi: "Suoliston terveys",
		fr: "Santé intestinale",
		hr: "Zdravlje crijeva",
		sl: "Zdravje črevesja",
		sr: "Zdravlje creva",
		bs: "Zdravlje crijeva",
		mk: "Здравје на цревата",
		lt: "Žarnyno sveikata",
		lv: "Zarnu veselība",
		et: "Soolestiku tervis",
		hu: "Bélegészség",
		it: "Salute intestinale",
		nb: "Tarmhelse",
		nl: "Darmgezondheid",
		pl: "Zdrowie jelit",
		pt: "Saúde Intestinal",
		"pt-br": "Saúde intestinal",
		ro: "Sănătate intestinală",
		sk: "Zdravie čriev",
		sv: "Tarmhälsa",
		tr: "Bağırsak Sağlığı",
	},
	Mitochondrial: {
		de: "Mitochondrial",
		en: "Mitochondrial",
		bg: "Митохондриално",
		cs: "Mitochondriální",
		da: "Mitokondriel",
		el: "Μιτοχονδριακά",
		es: "Mitocondrial",
		fi: "Mitokondriaalinen",
		fr: "Mitochondrial",
		hr: "Mitohondrijsko zdravlje",
		sl: "Mitohondrijsko zdravje",
		sr: "Mitohondrijsko zdravlje",
		bs: "Mitohondrijski",
		mk: "Митохондриско здравје",
		lt: "Mitochondrijų sveikata",
		lv: "Mitohondriju veselība",
		et: "Mitokondriaalne",
		hu: "Mitokondriális",
		it: "Mitocondriale",
		nb: "Mitokondriell",
		nl: "Mitochondriaal",
		pl: "Mitochondrialne",
		pt: "Mitocondrial",
		"pt-br": "Mitocondrial",
		ro: "Mitocondrial",
		sk: "Mitochondriálne",
		sv: "Mitokondriell",
		tr: "Mitokondriyal",
	},
	Metabolic: {
		de: "Metabolisch",
		en: "Metabolic",
		bg: "Метаболитно",
		cs: "Metabolický",
		da: "Metabolisk",
		el: "Μεταβολικά",
		es: "Metabólico",
		fi: "Aineenvaihdunta",
		fr: "Métabolique",
		hr: "Metabolizam",
		sl: "Presnova",
		sr: "Metabolizam",
		bs: "Metabolički",
		mk: "Метаболизам",
		lt: "Metabolizmas",
		lv: "Vielmaiņa",
		et: "Ainevahetuslik",
		hu: "Metabolikus",
		it: "Metabolico",
		nb: "Metabolsk",
		nl: "Metabolisme",
		pl: "Metaboliczne",
		pt: "Metabólico",
		"pt-br": "Metabólico",
		ro: "Metabolic",
		sk: "Metabolické",
		sv: "Metabol",
		tr: "Metabolik",
	},
	Longevity: {
		de: "Langlebigkeit",
		en: "Longevity",
		bg: "Дълголетие",
		cs: "Dlouhověkost",
		da: "Levetid",
		el: "Μακροζωία",
		es: "Longevidad",
		fi: "Pitkäikäisyys",
		fr: "Longévité",
		hr: "Dugovječnost",
		sl: "Dolgoživost",
		sr: "Dugovečnost",
		bs: "Dugovječnost",
		mk: "Долговечност",
		lt: "Ilgaamžiškumas",
		lv: "Ilgmūžība",
		et: "Pikaelulisus",
		hu: "Élettartam",
		it: "Longevità",
		nb: "Livslengde",
		nl: "Levensduur",
		pl: "Długowieczność",
		pt: "Longevidade",
		"pt-br": "Longevidade",
		ro: "Longevitate",
		sk: "Dlhovekosť",
		sv: "Livslängd",
		tr: "Uzun Ömür",
	},
	"Skin & Tanning Research": {
		de: "Haut- & Bräunungsforschung",
		en: "Skin & Tanning Research",
		bg: "Кожа и изследвания на тен",
		cs: "Výzkum pokožky a opalování",
		da: "Hud- og solbruningsforskning",
		el: "Δέρμα & Έρευνα μαυρίσματος",
		es: "Investigación en piel y bronceado",
		fi: "Iho- ja rusketustutkimus",
		fr: "Peau et bronzage",
		hr: "Istraživanje kože i tamnjenja",
		sl: "Raziskave kože in porjavitve",
		sr: "Istraživanje kože i tamnjenja",
		bs: "Istraživanje kože i tamnjenja",
		mk: "Истражување на кожа и тен",
		lt: "Odos ir įdegio tyrimai",
		lv: "Ādas un iedeguma pētniecība",
		et: "Naha ja päevitumise uurimine",
		hu: "Bőr- és barnuláskutatás",
		it: "Ricerca su pelle e abbronzatura",
		nb: "Hud- og bruningsforskning",
		nl: "Huid- en bruiningsonderzoek",
		pl: "Badania nad skórą i opalaniem",
		pt: "Investigação em Pele e Bronzeamento",
		"pt-br": "Pesquisa em pele e bronzeamento",
		ro: "Cercetare piele și bronzare",
		sk: "Výskum pokožky a opaľovania",
		sv: "Hud- och solbränneforskning",
		tr: "Cilt ve Bronzlaşma Araştırması",
	},
	Neuroprotection: {
		de: "Neuroprotektion",
		en: "Neuroprotection",
		bg: "Невропротекция",
		cs: "Neuroprotekce",
		da: "Neurobeskyttelse",
		el: "Νευροπροστασία",
		es: "Neuroprotección",
		fi: "Neuroprotektio",
		fr: "Neuroprotection",
		hr: "Neuroprotekcija",
		sl: "Nevrozaščita",
		sr: "Neuroprotekcija",
		bs: "Neuroprotekcija",
		mk: "Невропротекција",
		lt: "Neuroapsauga",
		lv: "Neiroaizsardzība",
		et: "Neurokaitse",
		hu: "Neuroprotekció",
		it: "Neuroprotezione",
		nb: "Nevrobeskyttelse",
		nl: "Neuroprotectie",
		pl: "Neuroprotekcja",
		pt: "Neuroproteção",
		"pt-br": "Neuroproteção",
		ro: "Neuroprotecție",
		sk: "Neuroprotekcia",
		sv: "Neuroprotektion",
		tr: "Nöroproteksiyon",
	},
	Cognitive: {
		de: "Kognition",
		en: "Cognitive",
		bg: "Когниция",
		cs: "Kognice",
		da: "Kognition",
		el: "Γνωστική λειτουργία",
		es: "Cognición",
		fi: "Kognitio",
		fr: "Cognition",
		hr: "Kognicija",
		sl: "Kognicija",
		sr: "Kognicija",
		bs: "Kognicija",
		mk: "Когниција",
		lt: "Pažinimo funkcijos",
		lv: "Izziņa",
		et: "Tunnetus",
		hu: "Kogníció",
		it: "Cognitivo",
		nb: "Kognisjon",
		nl: "Cognitie",
		pl: "Kognitywne",
		pt: "Cognitivo",
		"pt-br": "Cognitivo",
		ro: "Cogniție",
		sk: "Kognitívne",
		sv: "Kognitiv",
		tr: "Biliş",
	},
	Immune: {
		de: "Immunsystem",
		en: "Immune",
		bg: "Имунна система",
		cs: "Imunita",
		da: "Immunforsvar",
		el: "Ανοσοποιητικό",
		es: "Sistema inmunitario",
		fi: "Immuniteetti",
		fr: "Système immunitaire",
		hr: "Imunološki sustav",
		sl: "Imunski sistem",
		sr: "Imunski sistem",
		bs: "Imunski sistem",
		mk: "Имун систем",
		lt: "Imuninė sistema",
		lv: "Imunitāte",
		et: "Immuunsüsteem",
		hu: "Immunrendszer",
		it: "Immunitario",
		nb: "Immunsystem",
		nl: "Immuunsysteem",
		pl: "Odpornościowe",
		pt: "Imunitário",
		"pt-br": "Imunológico",
		ro: "Imunitar",
		sk: "Imunitné",
		sv: "Immunförsvar",
		tr: "Bağışıklık",
	},
	"Immune Support": {
		de: "Immununterstützung",
		en: "Immune Support",
		bg: "Имунна подкрепа",
		cs: "Podpora imunity",
		da: "Immunstøtte",
		el: "Ενίσχυση ανοσοποιητικού",
		es: "Apoyo inmunitario",
		fi: "Immuunituki",
		fr: "Soutien immunitaire",
		hr: "Imunološka potpora",
		sl: "Imunska podpora",
		sr: "Imunološka podrška",
		bs: "Imunološka podrška",
		mk: "Имунолошка поддршка",
		lt: "Imuninės sistemos palaikymas",
		lv: "Imunitātes atbalsts",
		et: "Immuunsüsteemi toetus",
		hu: "Immunrendszer-támogatás",
		it: "Supporto immunitario",
		nb: "Immunstøtte",
		nl: "Immuunondersteuning",
		pl: "Wsparcie odporności",
		pt: "Suporte Imunitário",
		"pt-br": "Suporte imunológico",
		ro: "Suport imunitar",
		sk: "Podpora imunity",
		sv: "Immunstöd",
		tr: "Bağışıklık Desteği",
	},
};

/**
 * Pharmacological family labels for the Stack Builder profiles
 * (`family` in src/config/peptide-stacks.ts).
 */
const PEPTIDE_FAMILY_LABELS: Record<string, Record<string, string>> = {
	"Long-Acting Amylin Analogue": {
		de: "Langwirksames Amylin-Analogon",
		en: "Long-Acting Amylin Analogue",
		bg: "Дълготраен амилинов аналог",
		cs: "Dlouhodobě působící analog amylinu",
		da: "Langtidsvirkende amylin-analog",
		el: "Ανάλογο αμυλίνης μακράς δράσης",
		es: "Análogo de amilina de acción prolongada",
		fi: "Pitkävaikutteinen amyliinianalogi",
		fr: "Analogue de l'amyline à action prolongée",
		hr: "Dugodjelujući analog amilina",
		sl: "Dolgo delujoči analog amilina",
		sr: "Dugodelujući analog amilina",
		bs: "Dugodjelujući analog amilina",
		mk: "Долгодејствувачки аналог на амилин",
		lt: "Ilgai veikiantis amilino analogas",
		lv: "Ilgstošas darbības amilīna analogs",
		et: "Pikaajalise toimega amüliini analoog",
		hu: "Hosszú hatású amilin-analóg",
		it: "Analogo dell'amilina a lunga durata d'azione",
		nb: "Langtidsvirkende amylin-analog",
		nl: "Langwerkend amyline-analoog",
		pl: "Długo działający analog amyliny",
		pt: "Análogo de Amilina de Ação Prolongada",
		"pt-br": "Análogo de amilina de longa duração",
		ro: "Analog de amilină cu acțiune prelungită",
		sk: "Dlhodobo účinkujúci analóg amylínu",
		sv: "Långverkande amylinanalog",
		tr: "Uzun Etkili Amilin Analoğu",
	},
	"Copper-Binding Tripeptide": {
		de: "Kupferbindendes Tripeptid",
		en: "Copper-Binding Tripeptide",
		bg: "Медсвързващ трипептид",
		cs: "Tripeptid vázající měď",
		da: "Kobberbindende tripeptid",
		el: "Τριπεπτίδιο δέσμευσης χαλκού",
		es: "Tripéptido de unión al cobre",
		fi: "Kuparia sitova tripeptidi",
		fr: "Tripeptide chélateur de cuivre",
		hr: "Tripeptid koji veže bakar",
		sl: "Tripeptid, ki veže baker",
		sr: "Tripeptid koji vezuje bakar",
		bs: "Tripeptid koji veže bakar",
		mk: "Трипептид што врзува бакар",
		lt: "Varį surišantis tripeptidas",
		lv: "Varu saistošs tripeptīds",
		et: "Vaske siduv tripeptiid",
		hu: "Rézkötő tripeptid",
		it: "Tripeptide legante il rame",
		nb: "Kobberbindende tripeptid",
		nl: "Kopergebonden tripeptide",
		pl: "Trójpeptyd wiążący miedź",
		pt: "Tripéptido Ligante de Cobre",
		"pt-br": "Tripeptídeo ligante de cobre",
		ro: "Tripeptidă cu legare de cupru",
		sk: "Meď viažuci tripeptid",
		sv: "Kopparbindande tripeptid",
		tr: "Bakır Bağlayıcı Tripeptit",
	},
	"Gastric Pentadecapeptide": {
		de: "Gastrisches Pentadecapeptid",
		en: "Gastric Pentadecapeptide",
		bg: "Стомашен пентадекапептид",
		cs: "Žaludeční pentadekapeptid",
		da: "Gastrisk pentadecapeptid",
		el: "Γαστρικό πενταδεκαπεπτίδιο",
		es: "Pentadecapéptido gástrico",
		fi: "Mahalaukun pentadekapeptidi",
		fr: "Pentadécapeptide gastrique",
		hr: "Gastrični pentadekapeptid",
		sl: "Želodčni pentadekapeptid",
		sr: "Gastrični pentadekapeptid",
		bs: "Gastrični pentadekapeptid",
		mk: "Гастричен пентадекапептид",
		lt: "Skrandžio pentadekapeptidas",
		lv: "Gastrisks pentadekapeptīds",
		et: "Gastraalne pentadekapeptiid",
		hu: "Gyomor-pentadekapeptid",
		it: "Pentadecapeptide gastrico",
		nb: "Gastrisk pentadekapeptid",
		nl: "Gastrisch pentadecapeptide",
		pl: "Pentadekapeptyd żołądkowy",
		pt: "Pentadecapéptido Gástrico",
		"pt-br": "Pentadecapeptídeo gástrico",
		ro: "Pentadecapeptidă gastrică",
		sk: "Gastrický pentadekapeptid",
		sv: "Gastrisk pentadekapeptid",
		tr: "Gastrik Pentadekapeptit",
	},
	"Thymosin Beta-4 Fragment": {
		de: "Thymosin-Beta-4-Fragment",
		en: "Thymosin Beta-4 Fragment",
		bg: "Фрагмент на тимозин бета-4",
		cs: "Fragment thymosinu beta-4",
		da: "Thymosin beta-4-fragment",
		el: "Θραύσμα θυμοσίνης βήτα-4",
		es: "Fragmento de timosina beta-4",
		fi: "Thymosin Beta-4 -fragmentti",
		fr: "Fragment de thymosine bêta-4",
		hr: "Fragment Thymosina Beta-4",
		sl: "Fragment timozina beta-4",
		sr: "Fragment timozina beta-4",
		bs: "Fragment timozina beta-4",
		mk: "Фрагмент на тимозин бета-4",
		lt: "Timozino beta-4 fragmentas",
		lv: "Timozīna beta-4 fragments",
		et: "Tümosiini beeta-4 fragment",
		hu: "Thymosin béta-4 fragment",
		it: "Frammento di timosina beta-4",
		nb: "Thymosin beta-4-fragment",
		nl: "Thymosine Beta-4-fragment",
		pl: "Fragment tymozyny beta-4",
		pt: "Fragmento de Timosina Beta-4",
		"pt-br": "Fragmento de Timosina Beta-4",
		ro: "Fragment de Timozină Beta-4",
		sk: "Fragment tymozínu beta-4",
		sv: "Thymosin beta-4-fragment",
		tr: "Timozin Beta-4 Fragmanı",
	},
	"Incretin Triple Agonist": {
		de: "Inkretin-Dreifachagonist",
		en: "Incretin Triple Agonist",
		bg: "Инкретинов троен агонист",
		cs: "Inkretinový trojitý agonista",
		da: "Incretin-tripelagonist",
		el: "Τριπλός αγωνιστής ινκρετίνης",
		es: "Agonista triple de incretinas",
		fi: "Inkretiinin kolmoisagonisti",
		fr: "Triple agoniste des incrétines",
		hr: "Trostruki agonist inkretina",
		sl: "Trojni agonist inkretina",
		sr: "Trostruki agonist inkretina",
		bs: "Trostruki agonist inkretina",
		mk: "Троен агонист на инкретин",
		lt: "Trigubas inkretino agonistas",
		lv: "Inkretīna trīskāršs agonists",
		et: "Inkretiini kolmikagonist",
		hu: "Inkretin hármas agonista",
		it: "Triplo agonista incretinico",
		nb: "Inkretin-trippelagonist",
		nl: "Incretine triple-agonist",
		pl: "Potrójny agonista inkretynowy",
		pt: "Agonista Triplo das Incretinas",
		"pt-br": "Agonista triplo de incretina",
		ro: "Agonist triplu de incretină",
		sk: "Trojitý inkretínový agonista",
		sv: "Inkretin-trippelagonist",
		tr: "İnkretin Üçlü Agonisti",
	},
	"Incretin Dual Agonist": {
		de: "Inkretin-Doppelagonist",
		en: "Incretin Dual Agonist",
		bg: "Инкретинов двоен агонист",
		cs: "Inkretinový duální agonista",
		da: "Incretin-dobbeltagonist",
		el: "Διπλός αγωνιστής ινκρετίνης",
		es: "Agonista dual de incretinas",
		fi: "Inkretiinin kaksoisagonisti",
		fr: "Double agoniste des incrétines",
		hr: "Dvostruki agonist inkretina",
		sl: "Dvojni agonist inkretina",
		sr: "Dvostruki agonist inkretina",
		bs: "Dvostruki agonist inkretina",
		mk: "Двоен агонист на инкретин",
		lt: "Dvigubas inkretino agonistas",
		lv: "Inkretīna divkāršs agonists",
		et: "Inkretiini kaksikagonist",
		hu: "Inkretin kettős agonista",
		it: "Doppio agonista incretinico",
		nb: "Inkretin-dobbeltagonist",
		nl: "Incretine duale agonist",
		pl: "Podwójny agonista inkretynowy",
		pt: "Agonista Duplo das Incretinas",
		"pt-br": "Agonista duplo de incretina",
		ro: "Agonist dual de incretină",
		sk: "Duálny inkretínový agonista",
		sv: "Inkretin-dubbelagonist",
		tr: "İnkretin İkili Agonisti",
	},
	"Melanocortin Peptide Analogue": {
		de: "Melanocortin-Peptidanalogon",
		en: "Melanocortin Peptide Analogue",
		bg: "Меланокортинов пептиден аналог",
		cs: "Analog melanokortinového peptidu",
		da: "Melanocortin-peptidanalog",
		el: "Ανάλογο πεπτιδίου μελανοκορτίνης",
		es: "Análogo peptídico de melanocortina",
		fi: "Melanokortiinipeptidianalogi",
		fr: "Analogue peptidique de la mélanocortine",
		hr: "Analog melanokortinskog peptida",
		sl: "Analog melanokortinskega peptida",
		sr: "Melanokortinski peptidni analog",
		bs: "Analog melanokortinskog peptida",
		mk: "Аналог на меланокортински пептид",
		lt: "Melanokortino peptido analogas",
		lv: "Melanokortīna peptīda analogs",
		et: "Melanokortiini peptiidanaloog",
		hu: "Melanokortin-peptid analóg",
		it: "Analogo peptidico della melanocortina",
		nb: "Melanokortin-peptidanalog",
		nl: "Melanocortine-peptide-analoog",
		pl: "Analog peptydu melanokortyny",
		pt: "Análogo Peptídico da Melanocortina",
		"pt-br": "Análogo peptídico de melanocortina",
		ro: "Analog peptidic de melanocortină",
		sk: "Analóg melanokortínového peptidu",
		sv: "Melanokortin-peptidanalog",
		tr: "Melanokortin Peptit Analoğu",
	},
	"ACTH Fragment Analog": {
		de: "ACTH-Fragment-Analogon",
		en: "ACTH Fragment Analog",
		bg: "Аналог на ACTH фрагмент",
		cs: "Analog fragmentu ACTH",
		da: "ACTH-fragmentanalog",
		el: "Ανάλογο θραύσματος ACTH",
		es: "Análogo del fragmento de ACTH",
		fi: "ACTH-fragmenttianalogi",
		fr: "Analogue de fragment d'ACTH",
		hr: "Analog ACTH fragmenta",
		sl: "Analog fragmenta ACTH",
		sr: "Analog ACTH fragmenta",
		bs: "Analog ACTH fragmenta",
		mk: "Аналог на ACTH-фрагмент",
		lt: "ACTH fragmento analogas",
		lv: "ACTH fragmenta analogs",
		et: "ACTH-fragmendi analoog",
		hu: "ACTH-fragment analóg",
		it: "Analogo del frammento di ACTH",
		nb: "ACTH-fragmentanalog",
		nl: "ACTH-fragmentanaloog",
		pl: "Analog fragmentu ACTH",
		pt: "Análogo de Fragmento de ACTH",
		"pt-br": "Análogo de fragmento de ACTH",
		ro: "Analog de fragment ACTH",
		sk: "Analóg fragmentu ACTH",
		sv: "ACTH-fragmentanalog",
		tr: "ACTH Fragman Analoğu",
	},
	"Anxiolytic Heptapeptide": {
		de: "Anxiolytisches Heptapeptid",
		en: "Anxiolytic Heptapeptide",
		bg: "Анксиолитичен хептапептид",
		cs: "Anxiolytický heptapeptid",
		da: "Anxiolytisk heptapeptid",
		el: "Αγχολυτικό επταπεπτίδιο",
		es: "Heptapéptido ansiolítico",
		fi: "Anksiolyyttinen heptapeptidi",
		fr: "Heptapeptide anxiolytique",
		hr: "Anksiolitički heptapeptid",
		sl: "Anksiolitični heptapeptid",
		sr: "Anksiolitički heptapeptid",
		bs: "Anksiolitički heptapeptid",
		mk: "Анксиолитички хептапептид",
		lt: "Anksiolitinis heptapeptidas",
		lv: "Anksiolītisks heptapeptīds",
		et: "Anksiolüütiline heptapeptiid",
		hu: "Anxiolitikus heptapeptid",
		it: "Eptapeptide ansiolitico",
		nb: "Anxiolytisk heptapeptid",
		nl: "Anxiolytisch heptapeptide",
		pl: "Heptapeptyd anksjolityczny",
		pt: "Heptapéptido Ansiolítico",
		"pt-br": "Heptapeptídeo ansiolítico",
		ro: "Heptapeptidă anxiolitică",
		sk: "Anxiolytický heptapeptid",
		sv: "Ångestdämpande heptapeptid",
		tr: "Anksiyolitik Heptapeptit",
	},
	"GHRH Analog": {
		de: "GHRH-Analogon",
		en: "GHRH Analog",
		bg: "Аналог на GHRH",
		cs: "Analog GHRH",
		da: "GHRH-analog",
		el: "Ανάλογο GHRH",
		es: "Análogo de GHRH",
		fi: "GHRH-analogi",
		fr: "Analogue de la GHRH",
		hr: "GHRH analog",
		sl: "Analog GHRH",
		sr: "Analog GHRH",
		bs: "Analog GHRH",
		mk: "Аналог на GHRH",
		lt: "GHRH analogas",
		lv: "GHRH analogs",
		et: "GHRH analoog",
		hu: "GHRH-analóg",
		it: "Analogo del GHRH",
		nb: "GHRH-analog",
		nl: "GHRH-analoog",
		pl: "Analog GHRH",
		pt: "Análogo da GHRH",
		"pt-br": "Análogo de GHRH",
		ro: "Analog GHRH",
		sk: "Analóg GHRH",
		sv: "GHRH-analog",
		tr: "GHRH Analoğu",
	},
	"GHRH Analog (Mod GRF 1-29)": {
		de: "GHRH-Analogon (Mod GRF 1-29)",
		en: "GHRH Analog (Mod GRF 1-29)",
		bg: "Аналог на GHRH (Mod GRF 1-29)",
		cs: "Analog GHRH (Mod GRF 1-29)",
		da: "GHRH-analog (Mod GRF 1-29)",
		el: "Ανάλογο GHRH (Mod GRF 1-29)",
		es: "Análogo de GHRH (Mod GRF 1-29)",
		fi: "GHRH-analogi (Mod GRF 1-29)",
		fr: "Analogue de la GHRH (Mod GRF 1-29)",
		hr: "GHRH analog (Mod GRF 1-29)",
		sl: "Analog GHRH (Mod GRF 1-29)",
		sr: "Analog GHRH (Mod GRF 1-29)",
		bs: "Analog GHRH (Mod GRF 1-29)",
		mk: "Аналог на GHRH (Mod GRF 1-29)",
		lt: "GHRH analogas (Mod GRF 1-29)",
		lv: "GHRH analogs (Mod GRF 1-29)",
		et: "GHRH analoog (Mod GRF 1-29)",
		hu: "GHRH-analóg (Mod GRF 1-29)",
		it: "Analogo del GHRH (Mod GRF 1-29)",
		nb: "GHRH-analog (Mod GRF 1-29)",
		nl: "GHRH-analoog (Mod GRF 1-29)",
		pl: "Analog GHRH (Mod GRF 1-29)",
		pt: "Análogo da GHRH (Mod GRF 1-29)",
		"pt-br": "Análogo de GHRH (Mod GRF 1-29)",
		ro: "Analog GHRH (Mod GRF 1-29)",
		sk: "Analóg GHRH (Mod GRF 1-29)",
		sv: "GHRH-analog (Mod GRF 1-29)",
		tr: "GHRH Analoğu (Mod GRF 1-29)",
	},
	"Copper Peptide Complex": {
		de: "Kupferpeptid-Komplex",
		en: "Copper Peptide Complex",
		bg: "Медно-пептиден комплекс",
		cs: "Měďnato-peptidový komplex",
		da: "Kobberpeptidkompleks",
		el: "Σύμπλοκο πεπτιδίου χαλκού",
		es: "Complejo peptídico de cobre",
		fi: "Kuparipeptidikompleksi",
		fr: "Complexe peptidique de cuivre",
		hr: "Kompleks bakrenog peptida",
		sl: "Kompleks bakrovega peptida",
		sr: "Kompleks bakarnog peptida",
		bs: "Kompleks bakrenog peptida",
		mk: "Комплекс на бакарен пептид",
		lt: "Vario peptidų kompleksas",
		lv: "Vara peptīda komplekss",
		et: "Vasepeptiidi kompleks",
		hu: "Rézpeptid-komplex",
		it: "Complesso peptidico del rame",
		nb: "Kobberpeptidkompleks",
		nl: "Koperpeptidecomplex",
		pl: "Kompleks peptydowo-miedziowy",
		pt: "Complexo Peptídico de Cobre",
		"pt-br": "Complexo peptídico de cobre",
		ro: "Complex peptidic de cupru",
		sk: "Komplex medi a peptidu",
		sv: "Kopparpeptidkomplex",
		tr: "Bakır Peptit Kompleksi",
	},
	"Recombinant Human Growth Hormone": {
		de: "Rekombinantes humanes Wachstumshormon",
		en: "Recombinant Human Growth Hormone",
		bg: "Рекомбинантен човешки растежен хормон",
		cs: "Rekombinantní lidský růstový hormon",
		da: "Rekombinant humant væksthormon",
		el: "Ανασυνδυασμένη ανθρώπινη αυξητική ορμόνη",
		es: "Hormona de crecimiento humana recombinante",
		fi: "Rekombinantti ihmisen kasvuhormoni",
		fr: "Hormone de croissance humaine recombinante",
		hr: "Rekombinantni humani hormon rasta",
		sl: "Rekombinantni humani rastni hormon",
		sr: "Rekombinantni humani hormon rasta",
		bs: "Rekombinantni humani hormon rasta",
		mk: "Рекомбинантен човечки хормон на раст",
		lt: "Rekombinantinis žmogaus augimo hormonas",
		lv: "Rekombinantais cilvēka augšanas hormons",
		et: "Rekombinantne inimese kasvuhormoon",
		hu: "Rekombináns humán növekedési hormon",
		it: "Ormone della crescita umano ricombinante",
		nb: "Rekombinant humant veksthormon",
		nl: "Recombinant humaan groeihormoon",
		pl: "Rekombinowany ludzki hormon wzrostu",
		pt: "Hormona do Crescimento Humana Recombinante",
		"pt-br": "Hormônio de crescimento humano recombinante",
		ro: "Hormon de creștere uman recombinant",
		sk: "Rekombinantný ľudský rastový hormón",
		sv: "Rekombinant humant tillväxthormon",
		tr: "Rekombinant İnsan Büyüme Hormonu",
	},
	"Pyridine Nucleotide Cofactor": {
		de: "Pyridinnukleotid-Cofaktor",
		en: "Pyridine Nucleotide Cofactor",
		bg: "Пиридинов нуклеотиден кофактор",
		cs: "Pyridinnukleotidový kofaktor",
		da: "Pyridinnukleotid-cofaktor",
		el: "Συνένζυμο πυριδινικού νουκλεοτιδίου",
		es: "Cofactor de nucleótido de piridina",
		fi: "Pyridiininukleotidikofaktori",
		fr: "Cofacteur nucléotidique pyridinique",
		hr: "Piridin-nukleotidni kofaktor",
		sl: "Piridinski nukleotidni kofaktor",
		sr: "Piridinski nukleotidni kofaktor",
		bs: "Piridin-nukleotidni kofaktor",
		mk: "Пиридин-нуклеотиден кофактор",
		lt: "Piridino nukleotido kofaktorius",
		lv: "Piridīna nukleotīda kofaktors",
		et: "Püridiinnukleotiidi kofaktor",
		hu: "Piridin-nukleotid kofaktor",
		it: "Cofattore nucleotidico piridinico",
		nb: "Pyridinnukleotid-kofaktor",
		nl: "Pyridinenucleotide-cofactor",
		pl: "Kofaktor nukleotydu pirydynowego",
		pt: "Cofator de Nucleótido de Piridina",
		"pt-br": "Cofator de nucleotídeo de piridina",
		ro: "Cofactor nucleotidic piridinic",
		sk: "Pyridínový nukleotidový kofaktor",
		sv: "Pyridinnukleotidkofaktor",
		tr: "Piridin Nükleotit Kofaktörü",
	},
	"Mitochondrial-Derived Peptide": {
		de: "Mitochondriales Peptid",
		en: "Mitochondrial-Derived Peptide",
		bg: "Пептид с митохондриален произход",
		cs: "Peptid odvozený z mitochondrií",
		da: "Mitokondrielt afledt peptid",
		el: "Πεπτίδιο μιτοχονδριακής προέλευσης",
		es: "Péptido derivado de mitocondrias",
		fi: "Mitokondrioperäinen peptidi",
		fr: "Peptide dérivé des mitochondries",
		hr: "Mitohondrijski peptid",
		sl: "Mitohondrijski peptid",
		sr: "Mitohondrijski peptid",
		bs: "Mitohondrijski peptid",
		mk: "Митохондриски пептид",
		lt: "Mitochondrijų kilmės peptidas",
		lv: "Mitohondriju izcelsmes peptīds",
		et: "Mitokondritest pärinev peptiid",
		hu: "Mitokondriális eredetű peptid",
		it: "Peptide di derivazione mitocondriale",
		nb: "Mitokondrielt peptid",
		nl: "Mitochondrieel afgeleid peptide",
		pl: "Peptyd pochodzenia mitochondrialnego",
		pt: "Peptídeo Derivado da Mitocôndria",
		"pt-br": "Peptídeo derivado de mitocôndria",
		ro: "Peptidă de origine mitocondrială",
		sk: "Mitochondriálne odvodený peptid",
		sv: "Mitokondriellt härledd peptid",
		tr: "Mitokondri Kaynaklı Peptit",
	},
	"alpha-MSH Tripeptide (Lys-Pro-Val)": {
		de: "Alpha-MSH-Tripeptid (Lys-Pro-Val)",
		en: "alpha-MSH Tripeptide (Lys-Pro-Val)",
		bg: "Алфа-MSH трипептид (Lys-Pro-Val)",
		cs: "Alfa-MSH tripeptid (Lys-Pro-Val)",
		da: "Alfa-MSH-tripeptid (Lys-Pro-Val)",
		el: "Τριπεπτίδιο alpha-MSH (Lys-Pro-Val)",
		es: "Tripéptido alfa-MSH (Lys-Pro-Val)",
		fi: "alpha-MSH-tripeptidi (Lys-Pro-Val)",
		fr: "Tripeptide alpha-MSH (Lys-Pro-Val)",
		hr: "Alfa-MSH tripeptid (Lys-Pro-Val)",
		sl: "Alfa-MSH tripeptid (Lys-Pro-Val)",
		sr: "Alfa-MSH tripeptid (Lys-Pro-Val)",
		bs: "Alfa-MSH tripeptid (Lys-Pro-Val)",
		mk: "Трипептид на алфа-MSH (Lys-Pro-Val)",
		lt: "alfa-MSH tripeptidas (Lys-Pro-Val)",
		lv: "Alfa-MSH tripeptīds (Lys-Pro-Val)",
		et: "alfa-MSH tripeptiid (Lys-Pro-Val)",
		hu: "alfa-MSH tripeptid (Lys-Pro-Val)",
		it: "Tripeptide alfa-MSH (Lys-Pro-Val)",
		nb: "Alpha-MSH-tripeptid (Lys-Pro-Val)",
		nl: "alfa-MSH-tripeptide (Lys-Pro-Val)",
		pl: "Trójpeptyd alfa-MSH (Lys-Pro-Val)",
		pt: "Tripéptido alfa-MSH (Lys-Pro-Val)",
		"pt-br": "Tripeptídeo alfa-MSH (Lys-Pro-Val)",
		ro: "Tripeptidă alfa-MSH (Lys-Pro-Val)",
		sk: "alfa-MSH tripeptid (Lys-Pro-Val)",
		sv: "alfa-MSH-tripeptid (Lys-Pro-Val)",
		tr: "alfa-MSH Tripeptidi (Lys-Pro-Val)",
	},
	"Thymic Immunopeptide (28-aa)": {
		de: "Thymus-Immunopeptid (28 AS)",
		en: "Thymic Immunopeptide (28-aa)",
		bg: "Тимусен имунопептид (28 АК)",
		cs: "Thymický imunopeptid (28 AK)",
		da: "Thymisk immunopeptid (28-aa)",
		el: "Θυμικό ανοσοπεπτίδιο (28 αμινοξέων)",
		es: "Inmunopéptido tímico (28 aa)",
		fi: "Kateenkorvan immuunipeptidi (28 aa)",
		fr: "Immunopeptide thymique (28 aa)",
		hr: "Timusni imunopeptid (28 AK)",
		sl: "Timusni imunopeptid (28 AK)",
		sr: "Timusni imunopeptid (28 AK)",
		bs: "Timusni imunopeptid (28 AK)",
		mk: "Тимусен имунопептид (28 аминокиселини)",
		lt: "Timusinis imunopeptidas (28 aminorūgščių)",
		lv: "Timusa imunopeptīds (28 aminoskābes)",
		et: "Tüümuse immunopeptiid (28 aminohapet)",
		hu: "Tímusz-immunopeptid (28 aminosav)",
		it: "Immunopeptide timico (28-aa)",
		nb: "Thymus-immunopeptid (28 aa)",
		nl: "Thymisch immunopeptide (28-aa)",
		pl: "Immunopeptyd grasiczy (28 aa)",
		pt: "Imunopeptídeo Tímico (28 aa)",
		"pt-br": "Imunopeptídeo tímico (28 aa)",
		ro: "Imunopeptidă timică (28 aa)",
		sk: "Tymický imunopeptid (28 aa)",
		sv: "Tymisk immunopeptid (28 aa)",
		tr: "Timik İmmünopeptit (28-aa)",
	},
};

/**
 * Trust-badge labels keyed by CANONICAL slug (src/lib/trust-badges-canonical.json).
 * Legacy Saleor variants resolve through the alias map in @/lib/trust-badges
 * before lookup; scripts/check-trust-badges.mjs guards the live vocabulary.
 */
export const TRUST_BADGE_LABELS: Record<string, Record<string, string>> = {
	"purity-99-hplc": {
		de: "≥99% Reinheit (HPLC)",
		en: "≥99% Purity (HPLC)",
		bg: "≥99% чистота (HPLC)",
		cs: "Čistota ≥99 % (HPLC)",
		da: "≥99% renhed (HPLC)",
		el: "≥99% Καθαρότητα (HPLC)",
		es: "Pureza ≥99% (HPLC)",
		fi: "≥99 % puhtaus (HPLC)",
		fr: "≥99% pureté (HPLC)",
		hr: "≥99% čistoća (HPLC)",
		sl: "≥99% čistote (HPLC)",
		sr: "≥99% čistoće (HPLC)",
		bs: "≥99% čistoće (HPLC)",
		mk: "≥99% чистота (HPLC)",
		lt: "≥99% grynumas (HPLC)",
		lv: "≥99% tīrība (HPLC)",
		et: "≥99% puhtus (HPLC)",
		hu: "≥99% tisztaság (HPLC)",
		it: "Purezza ≥99% (HPLC)",
		nb: "≥99% renhet (HPLC)",
		nl: "≥99% zuiverheid (HPLC)",
		pl: "≥99% czystości (HPLC)",
		pt: "Pureza ≥99% (HPLC)",
		"pt-br": "≥99% de pureza (HPLC)",
		ro: "≥99% puritate (HPLC)",
		sk: "≥99 % čistota (HPLC)",
		sv: "≥99% renhet (HPLC)",
		tr: "≥%99 Saflık (HPLC)",
	},
	"coa-available": {
		de: "CoA verfügbar",
		en: "COA Available",
		bg: "CoA налично",
		cs: "CoA k dispozici",
		da: "CoA tilgængelig",
		el: "Διαθέσιμο πιστοποιητικό ανάλυσης (CoA)",
		es: "CoA disponible",
		fi: "CoA saatavilla",
		fr: "CoA disponible",
		hr: "CoA dostupan",
		sl: "CoA na voljo",
		sr: "CoA dostupan",
		bs: "CoA dostupan",
		mk: "Достапен CoA",
		lt: "CoA prieinamas",
		lv: "CoA pieejams",
		et: "CoA saadaval",
		hu: "CoA elérhető",
		it: "CoA disponibile",
		nb: "CoA tilgjengelig",
		nl: "CoA beschikbaar",
		pl: "Dostępny CoA",
		pt: "CoA Disponível",
		"pt-br": "CoA disponível",
		ro: "CoA disponibil",
		sk: "CoA k dispozícii",
		sv: "Analysintyg (CoA) tillgängligt",
		tr: "Analiz Sertifikası Mevcut",
	},
	"gmp-compliant": {
		de: "GMP-konform",
		en: "GMP-Compliant",
		bg: "GMP съответствие",
		cs: "Shoda s GMP",
		da: "GMP-overholdelse",
		el: "Συμμόρφωση με GMP",
		es: "Cumplimiento GMP",
		fi: "GMP:n mukainen",
		fr: "Conforme GMP",
		hr: "Sukladno GMP-u",
		sl: "Skladno z GMP",
		sr: "Saglasno sa GMP",
		bs: "Usklađeno s GMP",
		mk: "Усогласено со GMP",
		lt: "Atitinka GMP",
		lv: "Atbilst GMP",
		et: "Vastab GMP-le",
		hu: "GMP szabványú",
		it: "Conforme GMP",
		nb: "GMP-samsvar",
		nl: "GMP-conform",
		pl: "Zgodność z GMP",
		pt: "Conformidade GMP",
		"pt-br": "Conformidade GMP",
		ro: "Conform GMP",
		sk: "V súlade s GMP",
		sv: "Uppfyller GMP",
		tr: "GMP Uyumlu",
	},
	"batch-documentation": {
		de: "Chargendokumentation",
		en: "Batch Documentation",
		bg: "Партидна документация",
		cs: "Dokumentace šarže",
		da: "Batchdokumentation",
		el: "Τεκμηρίωση παρτίδας",
		es: "Documentación de lote",
		fi: "Eräkohtainen dokumentaatio",
		fr: "Documentation de lot",
		hr: "Dokumentacija serije",
		sl: "Dokumentacija serije",
		sr: "Dokumentacija serije",
		bs: "Dokumentacija serije",
		mk: "Документација на серијата",
		lt: "Partijos dokumentacija",
		lv: "Partijas dokumentācija",
		et: "Partii dokumentatsioon",
		hu: "Sarzsdokumentáció",
		it: "Documentazione di lotto",
		nb: "Batchdokumentasjon",
		nl: "Batchdocumentatie",
		pl: "Dokumentacja partii",
		pt: "Documentação de Lote",
		"pt-br": "Documentação de lote",
		ro: "Documentație de lot",
		sk: "Dokumentácia šarže",
		sv: "Batchdokumentation",
		tr: "Parti Dokümantasyonu",
	},
	"third-party-tested": {
		de: "Extern laborgeprüft",
		en: "Third-Party Lab Tested",
		bg: "Тествано от независима лаборатория",
		cs: "Testováno nezávislou laboratoří",
		da: "Testet af uafhængigt laboratorium",
		el: "Ελεγμένο από ανεξάρτητο εργαστήριο",
		es: "Analizado por laboratorio independiente",
		fi: "Riippumattoman laboratorion testaama",
		fr: "Testé par un laboratoire indépendant",
		hr: "Testirano u neovisnom laboratoriju",
		sl: "Testirano v neodvisnem laboratoriju",
		sr: "Testirano u nezavisnoj laboratoriji",
		bs: "Testirano u nezavisnoj laboratoriji",
		mk: "Тестирано во независна лабораторија",
		lt: "Ištirta nepriklausomoje laboratorijoje",
		lv: "Pārbaudīta neatkarīgā laboratorijā",
		et: "Testitud sõltumatus laboris",
		hu: "Külső laborban ellenőrizve",
		it: "Testato da laboratorio indipendente",
		nb: "Eksternt laboratorietestet",
		nl: "Getest door onafhankelijk lab",
		pl: "Testowane przez niezależne laboratorium",
		pt: "Testado por Laboratório Independente",
		"pt-br": "Testado por laboratório independente",
		ro: "Testat de laborator independent",
		sk: "Testované nezávislým laboratóriom",
		sv: "Testad av oberoende labb",
		tr: "Bağımsız Laboratuvarda Test Edilmiş",
	},
	"research-use-only": {
		de: "Nur für Forschungszwecke",
		en: "Research Use Only",
		bg: "Само за изследователски цели",
		cs: "Pouze pro výzkumné účely",
		da: "Kun til forskningsbrug",
		el: "Μόνο για ερευνητική χρήση",
		es: "Solo para uso en investigación",
		fi: "Vain tutkimuskäyttöön",
		fr: "Usage recherche uniquement",
		hr: "Samo za istraživačke svrhe",
		sl: "Samo za raziskovalne namene",
		sr: "Samo u istraživačke svrhe",
		bs: "Samo za istraživačke svrhe",
		mk: "Само за истражувачки цели",
		lt: "Tik moksliniams tyrimams",
		lv: "Tikai pētniecībai",
		et: "Ainult teadusuuringuteks",
		hu: "Kizárólag kutatási célra",
		it: "Solo per uso di ricerca",
		nb: "Kun til forskningsformål",
		nl: "Uitsluitend voor onderzoeksdoeleinden",
		pl: "Wyłącznie do celów badawczych",
		pt: "Exclusivamente para Investigação",
		"pt-br": "Exclusivamente para pesquisa",
		ro: "Exclusiv pentru uz de cercetare",
		sk: "Len na výskumné účely",
		sv: "Endast för forskningsändamål",
		tr: "Yalnızca Araştırma Amaçlı",
	},
	"lyophilized-powder": {
		de: "Lyophilisiertes Pulver",
		en: "Lyophilized Powder",
		bg: "Лиофилизиран прах",
		cs: "Lyofilizovaný prášek",
		da: "Frysetørret pulver",
		el: "Λυοφιλοποιημένη σκόνη",
		es: "Polvo liofilizado",
		fi: "Lyofilisoitu jauhe",
		fr: "Poudre lyophilisée",
		hr: "Liofilizirani prašak",
		sl: "Liofiliziran prašek",
		sr: "Liofilizovan prah",
		bs: "Liofilizirani prah",
		mk: "Лиофилизиран прашок",
		lt: "Liofilizuoti milteliai",
		lv: "Liofilizēts pulveris",
		et: "Lüofiliseeritud pulber",
		hu: "Liofilizált por",
		it: "Polvere liofilizzata",
		nb: "Lyofilisert pulver",
		nl: "Gelyofiliseerd poeder",
		pl: "Proszek liofilizowany",
		pt: "Pó Liofilizado",
		"pt-br": "Pó liofilizado",
		ro: "Pulbere liofilizată",
		sk: "Lyofilizovaný prášok",
		sv: "Lyofiliserat pulver",
		tr: "Liyofilize Toz",
	},
	sterile: {
		de: "Steril",
		en: "Sterile",
		bg: "Стерилно",
		cs: "Sterilní",
		da: "Steril",
		el: "Αποστειρωμένο",
		es: "Estéril",
		fi: "Steriili",
		fr: "Stérile",
		hr: "Sterilno",
		sl: "Sterilno",
		sr: "Sterilno",
		bs: "Sterilno",
		mk: "Стерилно",
		lt: "Sterilu",
		lv: "Sterils",
		et: "Steriilne",
		hu: "Steril",
		it: "Sterile",
		nb: "Steril",
		nl: "Steriel",
		pl: "Sterylny",
		pt: "Estéril",
		"pt-br": "Estéril",
		ro: "Steril",
		sk: "Sterilné",
		sv: "Steril",
		tr: "Steril",
	},
	"usp-grade": {
		de: "USP-konform",
		en: "USP Grade",
		bg: "USP съответствие",
		cs: "Shoda s USP",
		da: "USP-overholdelse",
		el: "Συμμόρφωση με USP",
		es: "Cumplimiento USP",
		fi: "USP-laatu",
		fr: "Qualité USP",
		hr: "USP kvaliteta",
		sl: "Skladno z USP",
		sr: "Saglasno sa USP",
		bs: "Usklađeno s USP",
		mk: "Усогласено со USP",
		lt: "Atitinka USP",
		lv: "Atbilst USP",
		et: "Vastab USP-le",
		hu: "USP minőség",
		it: "Qualità USP",
		nb: "USP-kvalitet",
		nl: "USP-kwaliteit",
		pl: "Jakość USP",
		pt: "Qualidade USP",
		"pt-br": "Qualidade USP",
		ro: "Calitate USP",
		sk: "Kvalita USP",
		sv: "USP-kvalitet",
		tr: "USP Kalitesi",
	},
	"pyrogen-free": {
		de: "Pyrogenfrei",
		en: "Pyrogen-Free",
		bg: "Апирогенно",
		cs: "Apyrogenní",
		da: "Pyrogenfri",
		el: "Απυρετογόνο",
		es: "Apirógeno",
		fi: "Pyrogeeniton",
		fr: "Sans pyrogène",
		hr: "Bez pirogena",
		sl: "Brez pirogenov",
		sr: "Bez pirogena",
		bs: "Bez pirogena",
		mk: "Без пирогени",
		lt: "Be pirogenų",
		lv: "Be pirogēniem",
		et: "Pürogeenivaba",
		hu: "Pirogénmentes",
		it: "Apirogeno",
		nb: "Pyrogenfri",
		nl: "Pyrogeenvrij",
		pl: "Bezpirogenowy",
		pt: "Apirogénico",
		"pt-br": "Apirogênico",
		ro: "Fără pirogeni",
		sk: "Bez pyrogénov",
		sv: "Pyrogenfri",
		tr: "Pirojensiz",
	},
};

const TIME_UNIT_LABELS: Record<string, Record<string, UnitLabel>> = {
	hours: {
		de: "Stunden",
		en: "hours",
		bg: "часа",
		cs: "hodin",
		da: "timer",
		el: "ώρες",
		es: "horas",
		fi: "tuntia",
		fr: "heures",
		hr: "sati",
		sl: "ur",
		sr: "sati",
		bs: "sati",
		mk: "часа",
		lt: { one: "valanda", few: "valandos", many: "valandos", other: "valandų" },
		lv: "stundas",
		et: "tundi",
		hu: "óra",
		it: "ore",
		nb: "timer",
		nl: "uur",
		pl: "godzin",
		pt: "horas",
		"pt-br": "horas",
		ro: "ore",
		sk: "hodín",
		sv: "timmar",
		tr: "saat",
	},
	hour: {
		de: "Stunde",
		en: "hour",
		bg: "час",
		cs: "hodina",
		da: "time",
		el: "ώρα",
		es: "hora",
		fi: "tunti",
		fr: "heure",
		hr: "sat",
		sl: "ura",
		sr: "sat",
		bs: "sat",
		mk: "час",
		lt: { one: "valanda", few: "valandos", many: "valandos", other: "valandų" },
		lv: "stunda",
		et: "tund",
		hu: "óra",
		it: "ora",
		nb: "time",
		nl: "uur",
		pl: "godzina",
		pt: "hora",
		"pt-br": "hora",
		ro: "oră",
		sk: "hodina",
		sv: "timme",
		tr: "saat",
	},
	days: {
		de: "Tage",
		en: "days",
		bg: "дни",
		cs: "dní",
		da: "dage",
		el: "ημέρες",
		es: "días",
		fi: "päivää",
		fr: "jours",
		hr: "dana",
		sl: "dni",
		sr: "dana",
		bs: "dana",
		mk: "дена",
		lt: { one: "diena", few: "dienos", many: "dienos", other: "dienų" },
		lv: "dienas",
		et: "päeva",
		hu: "nap",
		it: "giorni",
		nb: "dager",
		nl: "dagen",
		pl: "dni",
		pt: "dias",
		"pt-br": "dias",
		ro: "zile",
		sk: "dní",
		sv: "dagar",
		tr: "gün",
	},
	day: {
		de: "Tag",
		en: "day",
		bg: "ден",
		cs: "den",
		da: "dag",
		el: "ημέρα",
		es: "día",
		fi: "päivä",
		fr: "jour",
		hr: "dan",
		sl: "dan",
		sr: "dan",
		bs: "dan",
		mk: "ден",
		lt: { one: "diena", few: "dienos", many: "dienos", other: "dienų" },
		lv: "diena",
		et: "päev",
		hu: "nap",
		it: "giorno",
		nb: "dag",
		nl: "dag",
		pl: "dzień",
		pt: "dia",
		"pt-br": "dia",
		ro: "zi",
		sk: "deň",
		sv: "dag",
		tr: "gün",
	},
	weeks: {
		de: "Wochen",
		en: "weeks",
		bg: "седмици",
		cs: "týdnů",
		da: "uger",
		el: "εβδομάδες",
		es: "semanas",
		fi: "viikkoa",
		fr: "semaines",
		hr: "tjedana",
		sl: "tednov",
		sr: "nedelja",
		bs: "sedmica",
		mk: "недели",
		lt: { one: "savaitė", few: "savaitės", many: "savaitės", other: "savaičių" },
		lv: "nedēļas",
		et: "nädalat",
		hu: "hét",
		it: "settimane",
		nb: "uker",
		nl: "weken",
		pl: "tygodni",
		pt: "semanas",
		"pt-br": "semanas",
		ro: "săptămâni",
		sk: "týždňov",
		sv: "veckor",
		tr: "hafta",
	},
	week: {
		de: "Woche",
		en: "week",
		bg: "седмица",
		cs: "týden",
		da: "uge",
		el: "εβδομάδα",
		es: "semana",
		fi: "viikko",
		fr: "semaine",
		hr: "tjedan",
		sl: "teden",
		sr: "nedelja",
		bs: "sedmica",
		mk: "недела",
		lt: { one: "savaitė", few: "savaitės", many: "savaitės", other: "savaičių" },
		lv: "nedēļa",
		et: "nädal",
		hu: "hét",
		it: "settimana",
		nb: "uke",
		nl: "week",
		pl: "tydzień",
		pt: "semana",
		"pt-br": "semana",
		ro: "săptămână",
		sk: "týždeň",
		sv: "vecka",
		tr: "hafta",
	},
	minutes: {
		de: "Minuten",
		en: "minutes",
		bg: "минути",
		cs: "minut",
		da: "minutter",
		el: "λεπτά",
		es: "minutos",
		fi: "minuuttia",
		fr: "minutes",
		hr: "minuta",
		sl: "minut",
		sr: "minuta",
		bs: "minuta",
		mk: "минути",
		lt: { one: "minutė", few: "minutės", many: "minutės", other: "minučių" },
		lv: "minūtes",
		et: "minutit",
		hu: "perc",
		it: "minuti",
		nb: "minutter",
		nl: "minuten",
		pl: "minut",
		pt: "minutos",
		"pt-br": "minutos",
		ro: "minute",
		sk: "minút",
		sv: "minuter",
		tr: "dakika",
	},
	minute: {
		de: "Minute",
		en: "minute",
		bg: "минута",
		cs: "minuta",
		da: "minut",
		el: "λεπτό",
		es: "minuto",
		fi: "minuutti",
		fr: "minute",
		hr: "minuta",
		sl: "minuta",
		sr: "minut",
		bs: "minuta",
		mk: "минута",
		lt: { one: "minutė", few: "minutės", many: "minutės", other: "minučių" },
		lv: "minūte",
		et: "minut",
		hu: "perc",
		it: "minuto",
		nb: "minutt",
		nl: "minuut",
		pl: "minuta",
		pt: "minuto",
		"pt-br": "minuto",
		ro: "minut",
		sk: "minúta",
		sv: "minut",
		tr: "dakika",
	},
};

/**
 * German → canonical English mappings for time units that appear in Saleor metadata.
 * Applied before TIME_UNIT_LABELS translation so German source values are handled.
 */
const DE_TO_EN_TIME_UNITS: Record<string, string> = {
	Stunden: "hours",
	Stunde: "hours",
	Tage: "days",
	Tag: "days",
	Wochen: "weeks",
	Woche: "week",
	Minuten: "minutes",
	Minute: "minutes",
};

/**
 * Common German phrases that appear in peptide metadata dosage/context fields.
 * Translated at the phrase level (after time unit replacement).
 */
const PHRASE_TRANSLATIONS: Record<string, Record<string, string>> = {
	Forschungskontext: {
		en: "research context",
		it: "contesto di ricerca",
		es: "contexto de investigación",
		fr: "contexte de recherche",
		nl: "onderzoekscontext",
		pt: "contexto de investigação",
		pl: "kontekst badawczy",
		de: "Forschungskontext",
	},
	"ein- bis zweimal täglich": {
		en: "once or twice daily",
		it: "una o due volte al giorno",
		es: "una o dos veces al día",
		fr: "une à deux fois par jour",
		nl: "één tot twee keer per dag",
		pt: "uma a duas vezes por dia",
		pl: "raz lub dwa razy dziennie",
		de: "ein- bis zweimal täglich",
	},
	subkutan: {
		en: "subcutaneous",
		it: "sottocutaneo",
		es: "subcutáneo",
		fr: "sous-cutané",
		nl: "subcutaan",
		pt: "subcutâneo",
		pl: "podskórnie",
		de: "subkutan",
	},
};

/**
 * Per-product translations for free-text metadata fields.
 * Keyed by product slug, then locale.
 * English entries are unnecessary - English is the raw metadata fallback.
 */
export const PEPTIDE_TRANSLATIONS: Record<string, Record<string, { mechanism?: string; dosage?: string }>> = {
	"glow-stack": {
		nl: {
			dosage:
				"Vaste onderzoeksblend per flacon: 50 mg GHK-Cu, 10 mg TB-500 en 10 mg BPC-157 (samen 70 mg aan opgegeven peptidehoeveelheden).",
		},
	},
	sermorelin: {
		nl: {
			dosage:
				"Onderzoeksprotocollen met sermoreline verschillen per studie en model; de beschreven hoeveelheden zijn studiespecifiek en vormen geen doseeradvies of gebruiksinstructie.",
		},
	},
	"ahk-cu": {
		de: {
			mechanism:
				"In den zitierten präklinischen Modellen war AHK-Cu mit einer Verlängerung isolierter menschlicher Haarfollikel ex vivo und einer Proliferation kultivierter humaner dermaler Papillenzellen in vitro assoziiert. Bei 10⁻⁹ M berichtete die Studie zudem ein höheres Bcl-2/Bax-Verhältnis sowie niedrigere Spiegel von gespaltener Caspase-3 und PARP; eine Verringerung der Zahl apoptotischer Zellen war statistisch nicht signifikant.",
		},
	},
	"bpc-157": {
		nl: {
			dosage:
				"Onderzoekscontext: gepubliceerde BPC-157-protocollen zijn modelspecifiek en vormen geen gebruiks- of doseringsadvies voor deze 5 mg-flacon.",
		},
		de: {
			mechanism:
				"Fördert die Expression von Wachstumshormonrezeptoren; moduliert VEGF und Angiogenese; unterstützt Kollagensynthese.",
			dosage: "250-500 mcg ein- bis zweimal täglich, subkutan",
		},
	},
	cagrilintide: {
		de: {
			mechanism:
				"Die präklinische Pharmakologie charakterisierte AM833, die als Cagrilintid bekannte Entwicklungssubstanz, als nichtselektiven Agonisten an den Amylinrezeptoren und am Calcitoninrezeptor. Diese Beschreibung auf Rezeptorebene erklärt die Forschungsrationale, belegt jedoch weder ein therapeutisches Ergebnis noch eine klinische Eignung oder eine Gleichwertigkeit zwischen diesem Forschungsmaterial und einem Prüfpräparat.",
		},
	},
	"cjc-1295": {
		nl: {
			dosage: "Gepubliceerde onderzoeksdoseringen verschillen per studieprotocol.",
		},
		de: {
			mechanism:
				"GHRH-Rezeptor-Agonist; stimuliert hypophysäre somatotrope Zellen zur pulsatilen GH-Freisetzung.",
			dosage: "100-300 mcg 1-3x täglich, subkutan (häufig mit einem GHRP kombiniert)",
		},
	},
	"cjc-ipa-blend": {
		nl: {
			dosage:
				"Onderzoek naar groeihormoonsecretagogen gebruikt studie- en modelspecifieke opzetten. Die vormen geen doseerschema voor deze blend en geven geen gebruiksaanwijzing voor mensen of dieren.",
		},
		de: {
			mechanism:
				"Dualer GH-Sekretagogen-Blend: CJC-1295 (ohne DAC) ist ein GHRH-Rezeptor-Agonist; Ipamorelin ist ein selektiver Ghrelin-/GHS-R1a-Agonist. Zusammen verstärken sie die pulsatile GH-Freisetzung aus hypophysären somatotropen Zellen.",
			dosage:
				"Nur Forschungskontext. Geliefert als lyophilisiertes Zwei-Peptid-Vial mit 5 mg + 5 mg, das für die Laborhandhabung mit bakteriostatischem Wasser rekonstituiert wird; kombinierte GHRH-plus-GHRP-Studiendesigns sind in der Forschungsliteratur beschrieben. Es wird keine Dosierung für Mensch oder Tier angegeben oder impliziert; dieses Produkt ist nicht zum Verzehr bestimmt.",
		},
	},
	epithalon: {
		nl: {
			dosage:
				"Onderzoeksprotocollen voor Epithalon verschillen per model en studieopzet; er is geen universele doseringsrichtlijn. Deze informatie is onderzoekscontext en geen doseringsadvies.",
		},
		de: {
			mechanism:
				"Synthetisches Pineal-Tetrapeptid (AEDG). In der Forschung reaktiviert es hTERT/Telomerase zur Verlängerung der Telomere, bindet an die TERT-Promotorregion und die Linker-Histone H1.3/H1.6 und reguliert die AANAT-vermittelte Melatoninsynthese hoch.",
			dosage:
				"Publizierte Forschungsprotokolle (In-vitro- und Tiermodelle) verabreichen Epithalon rekonstituiert in bakteriostatischem oder sterilem Wasser in kurzen zyklischen Kursen (Forschungskontext). Ausschließlich für die In-vitro- und präklinische Laborforschung bestimmt, nicht für den menschlichen Verzehr; es wird keine Dosierungsempfehlung für den Menschen gegeben.",
		},
	},
	"ghk-cu": {
		nl: {
			dosage:
				"GHK-Cu-doseeronderzoek is modelafhankelijk: studies gebruiken uiteenlopende concentraties in cel- en diermodellen. Dit is onderzoekscontext, geen menselijk doseeradvies.",
		},
		de: {
			mechanism:
				"Bindet Kupfer(II)-Ionen; aktiviert Gene des Geweberemodellings; fördert die Synthese von Kollagen, Elastin und Glykosaminoglykanen.",
			dosage: "1-2 mg täglich, subkutan oder topisch (Creme/Serum)",
		},
	},
	ipamorelin: {
		nl: {
			dosage:
				"Gepubliceerde doseergegevens horen bij afzonderlijke onderzoeksprotocollen; ze vormen geen doseringsadvies voor dit onderzoeksmateriaal.",
		},
		de: {
			mechanism:
				"Selektiver GHS-R1a-Agonist (Ghrelinrezeptor) an hypophysären somatotropen Zellen; aktiviert Gq/PLC-Calcium-Signalwege und treibt so die pulsatile GH-Freisetzung an, ohne Cortisol oder Prolaktin zu erhöhen.",
			dosage:
				"In publizierter Labor- und Tierforschung wurde Ipamorelin parenteral in rekonstituierter Form verabreicht, mit berichteten Studiendosen im Bereich von wenigen Mikrogramm pro Kilogramm bis zu niedrigen Hundert-Mikrogramm-Mengen pro Gabe (Forschungskontext). Bergdorf Bioscience liefert dieses Peptid ausschließlich für die In-vitro- und Laborforschung; es ist nicht für die Anwendung an Mensch oder Tier bestimmt, und es wird keine Dosierung für den Menschen impliziert.",
		},
	},
	"klow-stack": {
		nl: {
			dosage:
				"De aangehaalde studies onderzoeken afzonderlijke componenten in cel- en diermodellen; zij beschrijven geen dosering van KLOW Stack als blend.",
		},
		de: {
			mechanism:
				"GHK-Cu: kupferabhängige Kollagensynthese und Matrix-Remodelling; TB-500: Aktin-Polymerisation und Zellmigration; BPC-157: Modulation des NO-Signalwegs und Angiogenese; KPV: NF-κB-Hemmung über α-MSH-Fragment-Aktivität.",
			dosage: "Gemäß jeweiligem Forschungsprotokoll (enthält 4 aktive Komponenten)",
		},
	},
	kpv: {
		nl: {
			dosage:
				"Onderzoeksprotocollen voor KPV verschillen per cel- en diermodel; er is geen gevalideerd doseringsschema voor mensen.",
		},
		de: {
			mechanism:
				"Hemmt die nukleäre Translokation von NF-κB; reduziert die Produktion proinflammatorischer Zytokine; abgeleitet von der C-terminalen Signalsequenz von α-MSH.",
			dosage: "200-500 mcg subkutan oder oral",
		},
	},
	"melanotan-1": {
		de: {
			mechanism:
				"Afamelanotid ist ein Melanocortinrezeptor-Agonist, der überwiegend an den Melanocortin-1-Rezeptor (MC1R) bindet. Die offizielle Scenesse-Fachinformation berichtet für das Retard-Implantat eine gesteigerte Eumelaninproduktion in der Haut unabhängig von UV-Exposition. Dieser Mechanismus liefert molekularen Forschungskontext und stellt keine kosmetische, bräunungsbezogene oder klinische Wirksamkeitsaussage für dieses Vial dar.",
		},
	},
	"mots-c": {
		nl: {
			dosage:
				"Gepubliceerde onderzoeksprotocollen verschillen per model en studieopzet; deze tekst geeft alleen algemene onderzoekscontext.",
		},
		de: {
			mechanism:
				"MOTS-c reguliert die metabolische Homöostase primär über den AMPK-Signalweg. Es moduliert den Folat-Methionin-Zyklus, was zur Akkumulation von AICAR führt, einem endogenen AMPK-Aktivator, der die zelluläre Glukoseaufnahme und die metabolische Flexibilität steigert. Unter metabolischem Stress transloziert MOTS-c von den Mitochondrien in den Zellkern und koreguliert die nukleäre Genexpression, einschließlich NRF2-abhängiger Gene der antioxidativen Antwort.",
			dosage: "5-10 mg pro Gabe (Forschungsdosis), rekonstituiert, subkutan; Forschungsprotokolle variieren",
		},
	},
	"mt-2": {
		nl: {
			dosage: "Gepubliceerde onderzoeksprotocollen verschillen per model en studie.",
		},
		de: {
			mechanism:
				"Nichtselektiver Melanocortinrezeptor-Agonismus: MC1R-vermittelte Eumelaninsynthese für die Bräunung, MC3R/MC4R-vermittelte hypothalamische Appetitunterdrückung, proerektile MC4R-Effekte über Oxytocin-Signalwege",
			dosage: "0,5-1 mg subkutan",
		},
	},
	"nad-plus": {
		nl: {
			dosage: "De onderzoeksopzet bepaalt de hoeveelheid. Deze productpagina geeft geen doseringsadvies.",
		},
		de: {
			mechanism:
				"Der NAD+/NADH-Redoxzyklus treibt den zellulären Energiestoffwechsel an; aktiviert Klasse-III-HDACs (Sirtuine SIRT1-7) für Langlebigkeits-Signalwege; Substrat für PARP1 in der DNA-Schadensantwort; Elektronenakzeptor am mitochondrialen Komplex I; wird durch CD38-NADasen verbraucht.",
			dosage: "100-500 mg täglich (Forschungsdosis), subkutan",
		},
	},
	"pt-141": {
		de: {
			mechanism:
				"Bremelanotid aktiviert nichtselektiv mehrere Melanocortinrezeptor-Subtypen. Die offizielle Vyleesi-Fachinformation berichtet folgende Potenzreihenfolge: MC1R, MC4R, MC3R, MC5R und MC2R, wobei die Bindung an MC1R und MC4R bei der therapeutischen Exposition des zugelassenen Arzneimittels als am relevantesten beschrieben wird. Diese Daten liefern Forschungskontext auf Rezeptorebene und keine Aussage zur klinischen Anwendung dieses Vials.",
		},
	},
	retatrutide: {
		nl: {
			dosage: "Uitsluitend voor wetenschappelijk onderzoek.",
		},
		de: {
			// Saleor dosage value is already German - mechanism only.
			mechanism:
				"Gleichzeitiger GLP-1R-, GIPR- und GCGR-Agonismus: GLP-1R steigert die Insulinsekretion und reduziert den Appetit; GIPR potenziert Insulin und mildert Übelkeit; GCGR stimuliert hepatische Lipolyse, Thermogenese und Energieverbrauch",
		},
	},
	selank: {
		nl: {
			dosage:
				"Gepubliceerde gegevens horen bij afzonderlijke onderzoeksprotocollen; ze zijn geen doseringsadvies voor dit onderzoeksmateriaal.",
		},
		de: {
			mechanism:
				"Moduliert den GABA-A-Rezeptor allosterisch; beeinflusst die BDNF- und NGF-Expression; erhöht die Stabilität von Enkephalinen.",
			dosage: "250-500 mcg, 1-3x täglich; intranasale Applikation in publizierten Studien",
		},
	},
	semax: {
		nl: {
			dosage:
				"Onderzoekscontext: studies naar Semax omvatten diermodellen voor geheugen, neurotrofinen en cerebrale ischemie.",
		},
		de: {
			mechanism:
				"Aktiviert Melanocortinrezeptoren (MC3/MC4); reguliert die Expression von BDNF, NGF und TrkB hoch.",
			dosage: "200-600 mcg 1-3x täglich, intranasal",
		},
	},
	"ss-31": {
		nl: {
			dosage:
				"Onderzoeksprotocollen voor SS-31 verschillen per experimenteel model en studieopzet; deze tekst geeft alleen algemene onderzoekscontext.",
		},
		de: {
			// Source: ux_mechanism / ux_typical_dosage on Saleor product UHJvZHVjdDozMg== (English).
			// Scientific figures (40 mg, dosing route, concentration ranges) are carried over verbatim.
			mechanism:
				"Mitochondrien-gerichtetes Tetrapeptid, das Cardiolipin in der inneren Mitochondrienmembran bindet, dadurch die Cristae stabilisiert, das Membranpotenzial erhält und die ATP-Synthese steigert, während reaktive Sauerstoffspezies reduziert werden.",
			dosage:
				"Nur als Referenz: In der klinischen Forschung wurde Elamipretide mit etwa 40 mg subkutan einmal täglich verabreicht (in früheren Studien auch als intravenöse Infusion), während in vitro im Labor üblicherweise nanomolare bis niedrig mikromolare Konzentrationen eingesetzt werden. Diese Angaben beschreiben publizierte Studiendesigns und sind weder eine Dosierungsanleitung noch eine Anwendungsempfehlung (Forschungskontext).",
		},
	},
	"tb-500": {
		nl: {
			dosage:
				"In gepubliceerde studies is TB-500 in verschillende laboratorium- en diermodellen onderzocht. De studieopzetten beschrijven onderzoekspreparaten en zijn geen gebruiks- of doseringsadvies.",
		},
		de: {
			mechanism:
				"G-Aktin-Sequestrierung als Treiber der Aktin-Polymerisation, ILK-PI3K-Akt-Zellüberlebenssignale, VEGF-vermittelte Angiogenese, MMP-getriebenes ECM-Remodelling, antiinflammatorische NF-κB-Modulation",
			dosage: "2,5-5 mg zweimal wöchentlich, subkutan",
		},
	},
	"tb500-bpc157-blend": {
		nl: {
			dosage:
				"Onderzoeksprotocollen zijn modelspecifiek en vormen geen doseringsadvies voor dit blendproduct.",
		},
		de: {
			mechanism:
				"TB-500: fördert Aktin-Polymerisation, Zellmigration und Angiogenese; BPC-157: moduliert den NO-Signalweg, fördert VEGF-Expression und Angiogenese, unterstützt die Kollagensynthese.",
			dosage: "Gemäß jeweiligem Forschungsprotokoll (enthält 2 aktive Komponenten)",
		},
	},
	tesamorelin: {
		nl: {
			dosage: "Onderzoekscontext: gepubliceerde hoeveelheden verschillen per studie en model.",
		},
		de: {
			// Saleor dosage value is already German - mechanism only.
			mechanism:
				"GHRH-Rezeptor-Agonist; stimuliert die pulsatile GH-Freisetzung; reduziert viszerales Fettgewebe; verbessert Triglyzeridwerte.",
		},
	},
	"thymosin-alpha-1": {
		nl: {
			dosage:
				"De beschreven toediening komt uit studies met thymalfasine en gezonde vrijwilligers; studieschema’s zijn geen gebruiksadvies en gelden niet automatisch voor dit onderzoeksproduct.",
		},
		de: {
			mechanism:
				"Fördert die T-Zell-Reifung über thymische Differenzierung; aktiviert dendritische Zellen und NK-Zellen; verstärkt TLR-vermittelte angeborene Immunsignale; moduliert die Th1/Th2-Balance.",
			dosage: "1,6 mg subkutan, zweimal wöchentlich",
		},
	},
	tirzepatid: {
		nl: {
			dosage:
				"Gepubliceerde onderzoeksdoseringen verschillen per studieprotocol; ze vormen geen gebruiksadvies voor dit onderzoeksmateriaal.",
		},
		de: {
			// Saleor dosage value is already German - mechanism only.
			mechanism:
				"Dualer GLP-1R- und GIPR-Agonismus: glukoseabhängige Insulinsekretion, Appetitunterdrückung über hypothalamische Sättigungsschaltkreise, verbesserte Insulinsensitivität im Fettgewebe, Aktivierung des braunen Fettgewebes über GIPR",
		},
	},
	vip: {
		de: {
			mechanism:
				"VIP interagiert mit den G-Protein-gekoppelten Rezeptoren VPAC1 und VPAC2. Humane Rezeptorexpressions- und Peptid-Struktur-Wirkungs-Studien zeigen eine Rezeptorbindung mit anschließender Stimulation der intrazellulären cAMP-Signalgebung; diese molekularen Beobachtungen belegen für sich genommen keine therapeutische Wirkung.",
		},
	},
};

/**
 * Chain locale whose entry supplies each free-text field of a product, field by field: es-mx takes
 * its own entry, then the Spanish one (`getContentFallbackChain`); every other locale only its own.
 * Null when no entry has the field, so the raw Saleor metadata stays. The PDP turns a base-locale
 * source into `lang` (spec 5.5).
 */
export function resolvePeptideTranslationSources(
	slug: string,
	locale: string,
): { mechanism: string | null; dosage: string | null } {
	const entries = PEPTIDE_TRANSLATIONS[slug];
	const chain = getContentFallbackChainOf(locale);
	return {
		mechanism: chain.find((candidate) => entries?.[candidate]?.mechanism != null) ?? null,
		dosage: chain.find((candidate) => entries?.[candidate]?.dosage != null) ?? null,
	};
}

/**
 * Apply the per-product free-text translations to parsed peptide metadata.
 *
 * Saleor stores `ux_mechanism` / `ux_typical_dosage` in a single language (mostly
 * English), so the PDP long-form article would otherwise render English copy on
 * localized routes. This overlay keys on locale only, never on channel — the same
 * German text has to show on `/de/eu/...` and `/de/us/...`. A regional locale
 * (es-mx) reads its base locale's entry for every field it does not translate itself.
 *
 * Pure: returns the input reference unchanged when no translation applies.
 */
export function applyPeptideTranslations(
	metadata: PeptideMetadata | null,
	slug: string,
	locale: string,
): PeptideMetadata | null {
	if (!metadata) return metadata;

	const sources = resolvePeptideTranslationSources(slug, locale);
	const entries = PEPTIDE_TRANSLATIONS[slug];
	const mechanism = (sources.mechanism && entries?.[sources.mechanism]?.mechanism) ?? metadata.mechanism;
	const typicalDosage = (sources.dosage && entries?.[sources.dosage]?.dosage) ?? metadata.typicalDosage;
	if (mechanism === metadata.mechanism && typicalDosage === metadata.typicalDosage) return metadata;

	return { ...metadata, mechanism, typicalDosage };
}

const DOSAGE_LOCALIZATION_REQUIRED: ReadonlySet<string> = new Set(["sl", "sr", "bs", "mk", "lt", "lv", "et"]);

/**
 * New language-wave PDPs show typical dosage only when reviewed copy exists in
 * that language. Preserve the existing locales' metadata fallback behaviour.
 */
export function dropUnlocalizedTypicalDosage(
	metadata: PeptideMetadata | null,
	slug: string,
	locale: string,
): PeptideMetadata | null {
	if (!metadata?.typicalDosage) return metadata;
	if (!DOSAGE_LOCALIZATION_REQUIRED.has(locale) || PEPTIDE_TRANSLATIONS[slug]?.[locale]?.dosage)
		return metadata;
	return { ...metadata, typicalDosage: null };
}

/** Translate a research level value. Falls back to English, then the raw value. */
export function translateResearchLevel(value: string, locale: string): string {
	const key = value.toLowerCase();
	return pickByContentChain(RESEARCH_LEVEL_LABELS[key], locale) ?? RESEARCH_LEVEL_LABELS[key]?.en ?? value;
}

/** Translate an availability value. Falls back to the raw value. */
export function translateAvailability(value: string, locale: string): string {
	return pickByContentChain(AVAILABILITY_LABELS[value], locale) ?? AVAILABILITY_LABELS[value]?.en ?? value;
}

/** Translate a research-area value from ux_peptide_categories_json. */
export function translatePeptideCategory(value: string, locale: string): string {
	return (
		pickByContentChain(PEPTIDE_CATEGORY_LABELS[value], locale) ?? PEPTIDE_CATEGORY_LABELS[value]?.en ?? value
	);
}

/** Translate a trust badge from ux_trust_badges_json (canonical slug or legacy variant). */
export function translateTrustBadge(value: string, locale: string): string {
	const alias = isCanonicalTrustBadge(value) ? value : resolveTrustBadgeAlias(value);
	const labels = TRUST_BADGE_LABELS[alias ?? value];
	if (!labels) {
		if (process.env.NODE_ENV !== "production") {
			console.warn(`[trust-badges] unknown badge value: ${JSON.stringify(value)}`);
		}
		return value;
	}
	return pickByContentChain(labels, locale) ?? labels.en ?? value;
}

/** Translate a Stack Builder pharmacological family label. */
export function translatePeptideFamily(value: string, locale: string): string {
	return (
		pickByContentChain(PEPTIDE_FAMILY_LABELS[value], locale) ?? PEPTIDE_FAMILY_LABELS[value]?.en ?? value
	);
}

/**
 * Replace time units in a string. Handles both English ("~4-6 hours") and
 * German ("~6 Tage") source values — normalizes German to English first,
 * then translates to target locale. Also translates common German phrases.
 */
export function translateTimeUnits(value: string, locale: string): string {
	let result = value;

	// Step 1: Normalize German time units to English canonical form
	for (const [de, en] of Object.entries(DE_TO_EN_TIME_UNITS)) {
		result = result.replace(new RegExp(`\\b${de}\\b`, "g"), en);
	}

	// Step 2: Translate English canonical form to target locale; plural forms follow
	// the number directly before the unit ("~4-6 hours" → the form for 6).
	for (const [en, translations] of Object.entries(TIME_UNIT_LABELS)) {
		const localized = pickByContentChain(translations, locale) ?? en;
		result = result.replace(new RegExp(`\\b${en}\\b`, "gi"), (_match, offset: number, text: string) =>
			selectUnitLabel(localized, locale, countBefore(text.slice(0, offset))),
		);
	}

	// Step 3: Translate common German phrases
	for (const [phrase, translations] of Object.entries(PHRASE_TRANSLATIONS)) {
		const localized = pickByContentChain(translations, locale) ?? phrase;
		if (result.includes(phrase)) {
			result = result.replace(phrase, localized);
		}
	}

	return result;
}
